import * as THREE from 'three';

console.log('background.js');

// TouchTexture class
class TouchTexture {
	constructor() {
		// this.size = 64;
		// this.width = this.height = this.size;
		// this.maxAge = 64;
		// this.radius = 0.25 * this.size; // Much larger touch radius for more obvious effect
		// this.speed = 0.1 / this.maxAge;
		// this.trail = [];
		// this.last = null;
		// this.initTexture();

		this.size = 64;
		this.width = this.height = this.size;
		this.maxAge = 160;
		this.radius = 0.25 * this.size;
		this.speed = 0.01 / this.maxAge;
		this.trail = [];
		this.last = null;
		this.initTexture();
	}

	initTexture() {
		console.log('initTexture');
		this.canvas = document.createElement("canvas");
		this.canvas.width = this.width;
		this.canvas.height = this.height;
		this.ctx = this.canvas.getContext("2d");
		this.ctx.fillStyle = "black";
		this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
		this.texture = new THREE.Texture(this.canvas);
	}

	update() {
		this.clear();
		let speed = this.speed;
		// Use reverse iteration to safely remove items
		for (let i = this.trail.length - 1; i >= 0; i--) {
			const point = this.trail[i];
			let f = point.force * speed * (1 - point.age / this.maxAge);
			point.x += point.vx * f;
			point.y += point.vy * f;
			point.age++;
			if (point.age > this.maxAge) {
				this.trail.splice(i, 1);
			} else {
				this.drawPoint(point);
			}
		}
		this.texture.needsUpdate = true;
	}

	clear() {
		this.ctx.fillStyle = "black";
		this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
	}

	addTouch(point) {
		let force = 0;
		let vx = 0;
		let vy = 0;
		const last = this.last;
		if (last) {
			const dx = point.x - last.x;
			const dy = point.y - last.y;
			if (dx === 0 && dy === 0) return;
			const dd = dx * dx + dy * dy;
			let d = Math.sqrt(dd);
			vx = dx / d;
			vy = dy / d;
			force = Math.min(dd * 50000, 2.2);
		}
		this.last = { x: point.x, y: point.y };
		this.trail.push({ x: point.x, y: point.y, age: 0, force, vx, vy });
	}

	drawPoint(point) {
		const pos = {
			x: point.x * this.width,
			y: (1 - point.y) * this.height
		};

		let intensity = 1;
		if (point.age < this.maxAge * 0.3) {
			intensity = Math.sin((point.age / (this.maxAge * 0.3)) * (Math.PI / 2));
		} else {
			const t = 1 - (point.age - this.maxAge * 0.3) / (this.maxAge * 0.7);
			intensity = -t * (t - 2);
		}
		intensity *= point.force;

		const radius = this.radius;
		let color = `${((point.vx + 1) / 2) * 255}, ${((point.vy + 1) / 2) * 255
			}, ${intensity * 255}`;
		let offset = this.size * 5;
		this.ctx.shadowOffsetX = offset;
		this.ctx.shadowOffsetY = offset;
		this.ctx.shadowBlur = radius * 1;
		this.ctx.shadowColor = `rgba(${color},${0.2 * intensity})`;

		this.ctx.beginPath();
		this.ctx.fillStyle = "rgba(255,0,0,1)";
		this.ctx.arc(pos.x - offset, pos.y - offset, radius, 0, Math.PI * 2);
		this.ctx.fill();
	}
}

// GradientBackground class
class GradientBackground {
	constructor(sceneManager) {
		this.sceneManager = sceneManager;
		this.mesh = null;
		this.uniforms = {
			uTime: { value: 0 },
			uResolution: {
				value: new THREE.Vector2(window.innerWidth, window.innerHeight)
			},
			uColor1: { value: new THREE.Vector3(0.937, 0.914, 0.902) }, // EFE9E6
			uColor2: { value: new THREE.Vector3(0.784, 0.698, 0.643) }, // C8B2A4
			uColor3: { value: new THREE.Vector3(0.937, 0.914, 0.902) }, // EFE9E6
			uColor4: { value: new THREE.Vector3(0.784, 0.698, 0.643) }, // C8B2A4
			uColor5: { value: new THREE.Vector3(0.937, 0.914, 0.902) }, // EFE9E6
			uColor6: { value: new THREE.Vector3(0.784, 0.698, 0.643) }, // C8B2A4

			uSpeed: { value: 0.8 },
			uIntensity: { value: 1.0 },
			uTouchTexture: { value: null },
			uGrainIntensity: { value: 0.03 },
			uZoom: { value: 1.0 }, // Zoom/scale control - lower = less zoomed (more visible)
			uDarkNavy: { value: new THREE.Vector3(0.937, 0.914, 0.902) }, // #EFE9E6 - Light base color
			uGradientSize: { value: 1.0 }, // Control gradient size (smaller = more gradients)
			uGradientCount: { value: 6.0 }, // Number of gradient centers
			uColor1Weight: { value: 1.0 }, // Weight for color1
			uColor2Weight: { value: 1.0 }, // Weight for color2
			uContrast: { value: 1.25 }, // Push away from midtones
			uScroll: { value: 0.0 } // Page scroll progress — primary motion
		};
	}

	init() {
		const viewSize = this.sceneManager.getViewSize();
		const geometry = new THREE.PlaneGeometry(
			viewSize.width,
			viewSize.height,
			1,
			1
		);

		const material = new THREE.ShaderMaterial({
			uniforms: this.uniforms,
			vertexShader: `
            varying vec2 vUv;
            void main() {
              vec3 pos = position.xyz;
              gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.);
              vUv = uv;
            }
          `,
			fragmentShader: `
            uniform float uTime;
            uniform vec2 uResolution;
            uniform vec3 uColor1;
            uniform vec3 uColor2;
            uniform vec3 uColor3;
            uniform vec3 uColor4;
            uniform vec3 uColor5;
            uniform vec3 uColor6;
            uniform float uSpeed;
            uniform float uIntensity;
            uniform sampler2D uTouchTexture;
            uniform float uGrainIntensity;
            uniform float uZoom;
            uniform vec3 uDarkNavy;
            uniform float uGradientSize;
            uniform float uGradientCount;
            uniform float uColor1Weight;
            uniform float uColor2Weight;
            uniform float uContrast;
            uniform float uScroll;
            
            varying vec2 vUv;
            
            #define PI 3.14159265359
            
            float grain(vec2 uv, float time) {
              vec2 grainUv = uv * uResolution * 0.5;
              float grainValue = fract(sin(dot(grainUv + time, vec2(12.9898, 78.233))) * 43758.5453);
              return grainValue * 2.0 - 1.0;
            }

            vec2 hash22(vec2 p) {
              p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));
              return fract(sin(p) * 43758.5453123) * 2.0 - 1.0;
            }

            float valueNoise(vec2 p) {
              vec2 i = floor(p);
              vec2 f = fract(p);
              vec2 u = f * f * (3.0 - 2.0 * f);
              float a = dot(hash22(i), f);
              float b = dot(hash22(i + vec2(1.0, 0.0)), f - vec2(1.0, 0.0));
              float c = dot(hash22(i + vec2(0.0, 1.0)), f - vec2(0.0, 1.0));
              float d = dot(hash22(i + vec2(1.0, 1.0)), f - vec2(1.0, 1.0));
              return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
            }

            float fbm3(vec2 p) {
              float v = 0.0;
              float a = 0.5;
              mat2 m = mat2(0.80, 0.60, -0.60, 0.80);
              for (int i = 0; i < 3; i++) {
                v += a * valueNoise(p);
                p = m * p * 2.02;
                a *= 0.5;
              }
              return v * 0.5 + 0.5;
            }

            float fbm(vec2 p) {
              float v = 0.0;
              float a = 0.5;
              mat2 m = mat2(0.80, 0.60, -0.60, 0.80);
              for (int i = 0; i < 4; i++) {
                v += a * valueNoise(p);
                p = m * p * 2.02;
                a *= 0.5;
              }
              return v * 0.5 + 0.5;
            }

            vec3 overlayBlend(vec3 base, vec3 blend, float opacity) {
              vec3 overlay = mix(
                2.0 * base * blend,
                1.0 - 2.0 * (1.0 - base) * (1.0 - blend),
                step(0.5, base)
              );
              return mix(base, overlay, clamp(opacity, 0.0, 1.0));
            }

            float crease(float n) {
              return smoothstep(0.35, 0.7, n);
            }
            
            vec3 getGradientColor(vec2 uv, float time) {
              float t = time * uSpeed;
              float s = uScroll;
              vec2 p = (uv - 0.5) * vec2(uResolution.x / max(uResolution.y, 1.0), 1.0);

              vec3 colDark = mix(uColor2, uColor4, 0.78);
              vec3 colMid = mix(uColor2, uColor5, 0.42);
              vec3 colLight = mix(uColor1, uColor5, 0.22);

              vec2 q = p * 1.2 + vec2(t * 0.03, s * 0.28);
              vec2 w1 = vec2(fbm3(q + vec2(0.0, 3.7)), fbm3(q + vec2(4.8, 1.1)));
              vec2 w2 = vec2(
                fbm3(q * 1.55 + w1 * 1.4 + vec2(2.3, 8.1)),
                fbm3(q * 1.55 + w1 * 1.4 + vec2(9.2, 3.4))
              );
              vec2 warp = (w1 * 0.72 + w2 * 0.5) * 2.0 - 1.0;

              vec2 flow = p * vec2(0.42, 1.5) + warp * 0.9 + vec2(sin(s * 6.28318) * 0.1, s * 0.58 + t * 0.022);
              float body = fbm(flow * 0.68);
              float phaseSoft = flow.y * 1.9 + flow.x * 0.32 + body * 1.2 + s * 1.5;
              float phaseFold = flow.y * 11.5 + flow.x * 0.95 + body * 1.15 + warp.x * 0.75 + s * 3.0;

              float softSheet = 0.5 + 0.5 * sin(phaseSoft);
              float midBand = 0.5 + 0.5 * sin(phaseSoft * 1.25 + 0.7);
              float ridge = abs(sin(phaseFold));
              float foldSharp = smoothstep(0.84, 0.995, ridge);
              float foldSoft = smoothstep(0.18, 0.82, ridge);
              float creaseAmt = crease(fbm3(flow * 0.42 + vec2(s * 0.22, t * 0.04)));
              float folds = mix(foldSoft, foldSharp, 0.42 + creaseAmt * 0.58);

              if (uGradientCount > 6.0) {
                float extraRidge = abs(sin(phaseFold * 1.28 + 0.4));
                float extra = mix(smoothstep(0.2, 0.8, extraRidge), smoothstep(0.86, 0.995, extraRidge), creaseAmt);
                folds = max(folds, extra * 0.5);
              }

              vec3 color = mix(colDark, colMid, body * 0.72 * uColor2Weight);
              color = mix(color, colMid, midBand * 0.18);
              color = overlayBlend(color, colMid, body * 0.06);
              color = mix(color, colLight, 0.04 + softSheet * 0.04 * uColor1Weight);
              color = mix(color, colDark, folds * 0.86);

              color = (color - 0.5) * uContrast + 0.5;

              float lum = dot(color, vec3(0.299, 0.587, 0.114));
              float cap = dot(uColor1, vec3(0.299, 0.587, 0.114)) * 0.96;
              if (lum > cap) {
                color *= cap / lum;
              }

              return clamp(color * uIntensity, vec3(0.0), vec3(1.0));
            }
            
            void main() {
              vec2 uv = vUv;
              
              // Apply water distortion from touch texture - very strong
              vec4 touchTex = texture2D(uTouchTexture, uv);
              float vx = -(touchTex.r * 2.0 - 1.0);
              float vy = -(touchTex.g * 2.0 - 1.0);
              float intensity = touchTex.b;
              // Much increased distortion strength for very obvious effect
              uv.x += vx * 0.8 * intensity;
              uv.y += vy * 0.8 * intensity;
              
              // Combined ripple and wave effect for better performance
              vec2 center = vec2(0.5);
              float dist = length(uv - center);
              float ripple = sin(dist * 20.0 - uTime * 3.0) * 0.04 * intensity;
              float wave = sin(dist * 15.0 - uTime * 2.0) * 0.03 * intensity;
              uv += vec2(ripple + wave);
              
              vec3 color = getGradientColor(uv, uTime);
              
              // Soft film grain — keep neutrals clean
              float grainValue = grain(uv, uTime);
              color += grainValue * uGrainIntensity;
              
              gl_FragColor = vec4(clamp(color, vec3(0.0), vec3(1.0)), 1.0);
            }
          `
		});

		this.mesh = new THREE.Mesh(geometry, material);
		this.mesh.position.z = 0;
		this.sceneManager.scene.add(this.mesh);
	}

	update(delta) {
		if (this.uniforms.uTime) {
			this.uniforms.uTime.value += delta;
		}
	}

	onResize(width, height) {
		const viewSize = this.sceneManager.getViewSize();
		if (this.mesh) {
			this.mesh.geometry.dispose();
			this.mesh.geometry = new THREE.PlaneGeometry(
				viewSize.width,
				viewSize.height,
				1,
				1
			);
		}
		if (this.uniforms.uResolution) {
			this.uniforms.uResolution.value.set(width, height);
		}
	}
}

// App class
class App {
	constructor() {
		this.renderer = new THREE.WebGLRenderer({
			antialias: true,
			powerPreference: "high-performance",
			alpha: false,
			stencil: false,
			depth: false
		});
		this.renderer.setSize(window.innerWidth, window.innerHeight);
		this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2)); // Cap pixel ratio for performance
		this.renderer.setAnimationLoop(null); // We'll use our own tick loop
		document.body.appendChild(this.renderer.domElement);
		this.renderer.domElement.id = "webGLApp";

		this.camera = new THREE.PerspectiveCamera(
			45,
			window.innerWidth / window.innerHeight,
			0.1,
			10000
		);
		this.camera.position.z = 50;
		this.scene = new THREE.Scene();
		this.scene.background = new THREE.Color(0xefe9e6); // EFE9E6
		this.clock = new THREE.Clock();

		this.touchTexture = new TouchTexture();
		this.gradientBackground = new GradientBackground(this);
		this.gradientBackground.uniforms.uTouchTexture.value = this.touchTexture.texture;
		this.scrollTarget = 0;
		this.scrollCurrent = 0;

		// Color schemes
		this.colorSchemes = {
			1: {
				// EFE9E6 + C8B2A4
				color1: new THREE.Vector3(0.937, 0.914, 0.902), // EFE9E6
				color2: new THREE.Vector3(0.784, 0.698, 0.643) // C8B2A4
			},
			2: {
				// Turquoise + Coral Red-Orange
				color1: new THREE.Vector3(1.0, 0.424, 0.314), // FF6C50 - Coral Red-Orange
				color2: new THREE.Vector3(0.251, 0.878, 0.816) // 40E0D0 - Turquoise
			},
			3: {
				// Orange + Navy + Turquoise (identical to scheme 1 but with turquoise added)
				color1: new THREE.Vector3(0.945, 0.353, 0.133), // F15A22 - Orange
				color2: new THREE.Vector3(0.039, 0.055, 0.153), // 0a0e27 - Navy Blue
				color3: new THREE.Vector3(0.251, 0.878, 0.816) // 40E0D0 - Turquoise
			},
			4: {
				// Based on Scheme 3: F26633 + 2D6B6D + D1AF9C
				color1: new THREE.Vector3(0.949, 0.4, 0.2), // F26633 - Orange/Coral
				color2: new THREE.Vector3(0.176, 0.42, 0.427), // 2D6B6D - Teal/Blue-Green
				color3: new THREE.Vector3(0.82, 0.686, 0.612) // D1AF9C - Beige/Peach
			},
			5: {
				// F15A22 + 004238 + F15A22 + 000000 + F15A22 + 000000
				color1: new THREE.Vector3(0.945, 0.353, 0.133), // F15A22 - Orange
				color2: new THREE.Vector3(0.0, 0.259, 0.22), // 004238 - Dark Teal (0, 66, 56)
				color3: new THREE.Vector3(0.945, 0.353, 0.133), // F15A22 - Orange
				color4: new THREE.Vector3(0.0, 0.0, 0.0), // 000000 - Black
				color5: new THREE.Vector3(0.945, 0.353, 0.133), // F15A22 - Orange
				color6: new THREE.Vector3(0.0, 0.0, 0.0) // 000000 - Black
			}
		};
		this.currentScheme = 1;

		this.init();
	}

	setColorScheme(scheme) {
		if (!this.colorSchemes[scheme]) return;
		this.currentScheme = scheme;
		const colors = this.colorSchemes[scheme];
		const uniforms = this.gradientBackground.uniforms;

		// Update all color uniforms
		if (scheme === 1) {
			// Light wash, mid taupe, and darker taupe for depth — stay in palette
			uniforms.uColor1.value.copy(colors.color1); // EFE9E6
			uniforms.uColor2.value.copy(colors.color2); // C8B2A4
			uniforms.uColor3.value.copy(colors.color1); // EFE9E6
			uniforms.uColor4.value.set(0.22, 0.16, 0.13); // dark taupe
			uniforms.uColor5.value.set(0.86, 0.78, 0.72); // mid-light beige
			uniforms.uColor6.value.set(0.34, 0.24, 0.2); // deep taupe
		} else if (scheme === 3) {
			// Scheme 3: Orange + Navy + Turquoise (identical to scheme 1 but with turquoise)
			uniforms.uColor1.value.copy(colors.color1); // Orange
			uniforms.uColor2.value.copy(colors.color2); // Navy
			uniforms.uColor3.value.copy(colors.color3); // Turquoise
			uniforms.uColor4.value.copy(colors.color1); // Orange
			uniforms.uColor5.value.copy(colors.color2); // Navy
			uniforms.uColor6.value.copy(colors.color3); // Turquoise
		} else if (scheme === 4) {
			// Scheme 4: Based on Scheme 3 with F26633, 2D6B6D, D1AF9C
			uniforms.uColor1.value.copy(colors.color1); // F26633 - Orange/Coral
			uniforms.uColor2.value.copy(colors.color2); // 2D6B6D - Teal/Blue-Green
			uniforms.uColor3.value.copy(colors.color3); // D1AF9C - Beige/Peach
			uniforms.uColor4.value.copy(colors.color1); // F26633 - Orange/Coral
			uniforms.uColor5.value.copy(colors.color2); // 2D6B6D - Teal/Blue-Green
			uniforms.uColor6.value.copy(colors.color3); // D1AF9C - Beige/Peach
		} else if (scheme === 5) {
			// Scheme 5: F15A22 + 004238 + F15A22 + 000000 + F15A22 + 000000
			uniforms.uColor1.value.copy(colors.color1); // F15A22 - Orange
			uniforms.uColor2.value.copy(colors.color2); // 004238 - Dark Teal
			uniforms.uColor3.value.copy(colors.color3); // F15A22 - Orange
			uniforms.uColor4.value.copy(colors.color4); // 000000 - Black
			uniforms.uColor5.value.copy(colors.color5); // F15A22 - Orange
			uniforms.uColor6.value.copy(colors.color6); // 000000 - Black
		} else {
			uniforms.uColor1.value.copy(colors.color1);
			uniforms.uColor2.value.copy(colors.color2);
			uniforms.uColor3.value.copy(colors.color1);
			uniforms.uColor4.value.copy(colors.color2);
			uniforms.uColor5.value.copy(colors.color1);
			uniforms.uColor6.value.copy(colors.color2);
		}

		// Update background color and base color
		if (scheme === 1) {
			this.scene.background = new THREE.Color(0xefe9e6); // EFE9E6 for scheme 1
			uniforms.uDarkNavy.value.set(0.82, 0.73, 0.68); // deeper taupe field so lights and darks both read
			uniforms.uGradientSize.value = 0.52;
			uniforms.uGradientCount.value = 12.0;
			uniforms.uSpeed.value = 0.18;
			uniforms.uIntensity.value = 1.0;
			uniforms.uGrainIntensity.value = 0.024;
			uniforms.uColor1Weight.value = 0.92;
			uniforms.uColor2Weight.value = 1.05;
			uniforms.uContrast.value = 1.3;
		} else if (scheme === 6) {
			// Scheme 6: Identical to scheme 1 but with Orange, Navy, and Turquoise
			this.scene.background = new THREE.Color(0x0a0e27); // Navy blue (same as scheme 1)
			uniforms.uDarkNavy.value.set(0.039, 0.055, 0.153); // #0a0e27 - Navy blue base color (same as scheme 1)
			// More gradient action: smaller gradients, more of them (same as scheme 1)
			uniforms.uGradientSize.value = 0.45; // Smaller gradient radius for more defined gradients
			uniforms.uGradientCount.value = 12.0; // More gradient centers (12 instead of 6)
			uniforms.uSpeed.value = 1.5; // Slightly faster for more movement
			// Balance colors: reduce orange, increase navy (same as scheme 1)
			uniforms.uColor1Weight.value = 0.5; // Reduce orange intensity
			uniforms.uColor2Weight.value = 1.8; // Increase navy intensity
			uniforms.uContrast.value = 1.18;
		} else if (scheme === 7) {
			// Scheme 7: Based on Scheme 6 with F26633, 2D6B6D, D1AF9C (same settings as Scheme 6)
			this.scene.background = new THREE.Color(0x0a0e27); // Navy blue (same as scheme 6)
			uniforms.uDarkNavy.value.set(0.039, 0.055, 0.153); // #0a0e27 - Navy blue base color (same as scheme 6)
			// More gradient action: smaller gradients, more of them (same as scheme 6)
			uniforms.uGradientSize.value = 0.45; // Smaller gradient radius for more defined gradients
			uniforms.uGradientCount.value = 12.0; // More gradient centers (12 instead of 6)
			uniforms.uSpeed.value = 1.5; // Slightly faster for more movement
			// Balance colors: same as scheme 6
			uniforms.uColor1Weight.value = 0.5; // Reduce orange/coral intensity
			uniforms.uColor2Weight.value = 1.8; // Increase teal intensity
			uniforms.uContrast.value = 1.18;
		} else if (scheme === 8) {
			// Scheme 8: Identical to Scheme 1
			this.scene.background = new THREE.Color(0x0a0e27); // Navy blue for scheme 8
			uniforms.uDarkNavy.value.set(0.039, 0.055, 0.153); // #0a0e27 - Navy blue base color
			// More gradient action: smaller gradients, more of them
			uniforms.uGradientSize.value = 0.45; // Smaller gradient radius for more defined gradients
			uniforms.uGradientCount.value = 12.0; // More gradient centers (12 instead of 6)
			uniforms.uSpeed.value = 1.5; // Slightly faster for more movement
			// Balance colors: reduce orange, increase navy
			uniforms.uColor1Weight.value = 0.5; // Reduce orange intensity
			uniforms.uColor2Weight.value = 1.8; // Increase navy intensity
			uniforms.uContrast.value = 1.18;
		} else if (scheme === 5) {
			// Scheme 5: Same settings as Scheme 1 but with F15A22 + 004238 + F15A22 + 000000 + F15A22 + 000000
			this.scene.background = new THREE.Color(0x0a0e27); // Navy blue for scheme 5 (same as scheme 1)
			uniforms.uDarkNavy.value.set(0.039, 0.055, 0.153); // #0a0e27 - Navy blue base color (same as scheme 1)
			// More gradient action: smaller gradients, more of them (same as scheme 1)
			uniforms.uGradientSize.value = 0.45; // Smaller gradient radius for more defined gradients
			uniforms.uGradientCount.value = 12.0; // More gradient centers (12 instead of 6)
			uniforms.uSpeed.value = 1.5; // Slightly faster for more movement
			// Balance colors: reduce orange, increase navy (same as scheme 1)
			uniforms.uColor1Weight.value = 0.5; // Reduce orange intensity
			uniforms.uColor2Weight.value = 1.8; // Increase navy intensity
			uniforms.uContrast.value = 1.18;
		} else if (scheme === 4) {
			this.scene.background = new THREE.Color(0xffffff); // Off-white for scheme 4
			uniforms.uDarkNavy.value.set(0, 0, 0); // #FAFAFA - Off-white base
			uniforms.uContrast.value = 1.22;
		} else if (scheme === 2) {
			this.scene.background = new THREE.Color(0x0a0e27); // Default dark navy for scheme 2
			uniforms.uDarkNavy.value.set(0.039, 0.055, 0.153); // #0a0e27 - Default dark navy
			uniforms.uGradientSize.value = 1.0; // Default size
			uniforms.uGradientCount.value = 6.0; // Default count
			uniforms.uSpeed.value = 1.2; // Default speed
			uniforms.uColor1Weight.value = 1.0; // Default weight
			uniforms.uColor2Weight.value = 1.0; // Default weight
			uniforms.uContrast.value = 1.2;
		} else {
			this.scene.background = new THREE.Color(0x0a0e27); // Default dark navy
			uniforms.uDarkNavy.value.set(0.039, 0.055, 0.153); // #0a0e27 - Default dark navy
			uniforms.uGradientSize.value = 1.0; // Default size
			uniforms.uGradientCount.value = 6.0; // Default count
			uniforms.uSpeed.value = 1.2; // Default speed
			uniforms.uColor1Weight.value = 1.0; // Default weight
			uniforms.uColor2Weight.value = 1.0; // Default weight
			uniforms.uContrast.value = 1.2;
		}
	}

	init() {
		this.gradientBackground.init();
		// Apply Scheme 1 settings on startup
		this.setColorScheme(1);

		// Force initial render to wake up the browser
		this.render();

		// Start animation loop
		this.tick();

		window.addEventListener("resize", () => this.onResize());
		window.addEventListener("scroll", () => this.onScroll(), { passive: true });

		// Seed the trail from the current scroll position
		this.onScroll();

		// Handle visibility changes to prevent throttling
		document.addEventListener("visibilitychange", () => {
			if (!document.hidden) {
				// Force render when page becomes visible
				this.render();
			}
		});

		// Wake up animation on any user interaction
		const wakeUpAnimation = () => {
			this.render();
			window.removeEventListener("click", wakeUpAnimation);
			window.removeEventListener("touchstart", wakeUpAnimation);
			window.removeEventListener("scroll", wakeUpAnimation);
		};
		window.addEventListener("click", wakeUpAnimation, { once: true });
		window.addEventListener("touchstart", wakeUpAnimation, { once: true });
		window.addEventListener("scroll", wakeUpAnimation, { once: true, passive: true });
	}

	onScroll() {
		const maxScroll = Math.max(
			1,
			document.documentElement.scrollHeight - window.innerHeight
		);
		this.scrollTarget = Math.min(1, Math.max(0, window.scrollY / maxScroll));
	}

	getViewSize() {
		const fovInRadians = (this.camera.fov * Math.PI) / 180;
		const height = Math.abs(
			this.camera.position.z * Math.tan(fovInRadians / 2) * 2
		);
		return { width: height * this.camera.aspect, height };
	}

	update(delta) {
		const maxScroll = Math.max(
			1,
			document.documentElement.scrollHeight - window.innerHeight
		);
		this.scrollTarget = Math.min(1, Math.max(0, window.scrollY / maxScroll));

		const lerp = 1 - Math.exp(-delta * 7);
		this.scrollCurrent += (this.scrollTarget - this.scrollCurrent) * lerp;

		if (this.gradientBackground.uniforms.uScroll) {
			this.gradientBackground.uniforms.uScroll.value = this.scrollCurrent;
		}

		const progress = this.scrollCurrent;
		this.scrollPoint = {
			x: 0.5 + Math.sin(progress * Math.PI * 2) * 0.38,
			y: 1 - progress
		};
		this.touchTexture.addTouch(this.scrollPoint);

		this.touchTexture.update();
		this.gradientBackground.update(delta);
	}

	render() {
		const delta = this.clock.getDelta();
		// Only update if delta is reasonable (prevents large jumps)
		const clampedDelta = Math.min(delta, 0.1);
		this.renderer.render(this.scene, this.camera);
		this.update(clampedDelta);
	}

	tick() {
		this.render();
		// Use arrow function to maintain context and ensure continuous rendering
		requestAnimationFrame(() => this.tick());
	}

	onResize() {
		this.camera.aspect = window.innerWidth / window.innerHeight;
		this.camera.updateProjectionMatrix();
		this.renderer.setSize(window.innerWidth, window.innerHeight);
		this.gradientBackground.onResize(window.innerWidth, window.innerHeight);
	}
}

// Convert RGB (0-1) to Hex
function rgbToHex(r, g, b) {
	const toHex = (n) => {
		const hex = Math.round(n * 255).toString(16);
		return hex.length === 1 ? "0" + hex : hex;
	};
	return "#" + toHex(r) + toHex(g) + toHex(b);
}

// Convert Hex to RGB (0-1)
function hexToRgb(hex) {
	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
	return result
		? {
			r: parseInt(result[1], 16) / 255,
			g: parseInt(result[2], 16) / 255,
			b: parseInt(result[3], 16) / 255
		}
		: null;
}


export const initBackground = () => {
	let pulseFrame = null
	let lastScrollTime = 0

	function checkPulse() {
		if (Date.now() - lastScrollTime > 100) {
			pulseFrame = null
		} else {
			pulseFrame = requestAnimationFrame(checkPulse)
		}
	}

	window.addEventListener("scroll", () => {
		lastScrollTime = Date.now()

		if (!pulseFrame) {
			pulseFrame = requestAnimationFrame(checkPulse)
		}

	}, { passive: true })

	// Start the app
	const app = new App();

	// Force animation to start immediately by triggering a render
	// This helps prevent browser throttling of requestAnimationFrame
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", () => {
			app.render();
		});
	} else {
		// DOM already loaded, force immediate render
		setTimeout(() => app.render(), 0);
	}
}